import { cn } from '@/lib/utils';
import React, { useState, useEffect } from 'react';
import { Tooltip, TooltipTrigger, TooltipContent } from './tooltip';

type AsProp = React.ElementType;

interface TruncatedTooltipProps {
  text?: string;
  lines?: number;
  maxWidth?: string;
  className?: string;
  as?: AsProp;
}

export function TruncatedTooltip({
  text,
  lines = 2,
  maxWidth = '100%',
  className,
  as: Tag = 'span',
}: TruncatedTooltipProps) {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    if (!el) return;

    const measure = () =>
      setIsTruncated(
        el.scrollHeight - el.clientHeight > 1 ||
          el.scrollWidth - el.clientWidth > 1
      );

    measure();

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(measure);
    observer.observe(el);

    return () => observer.disconnect();
  }, [el, text, lines, maxWidth]);

  const content = (
    <Tag
      ref={setEl}
      style={{
        WebkitLineClamp: lines,
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        maxWidth,
      }}
      className={cn('overflow-hidden wrap-anywhere', className)}
    >
      {text}
    </Tag>
  );

  if (!isTruncated) return content;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent
        sideOffset={4}
        className="max-w-xs px-3 py-2 text-sm text-brand-primary! bg-white shadow-md rounded-md whitespace-pre-wrap wrap-anywhere"
      >
        {text}
      </TooltipContent>
    </Tooltip>
  );
}
